import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Monitor, Tablet, Smartphone, Lock, ChevronDown, Play, Loader2,
  Building2, ShoppingBag, KeyRound, Cookie, X,
} from "lucide-react";
import { ENV_MODES, VIEWPORTS, createSim } from "@/sim/lib";
import { cn } from "@/lib/utils";

const VP_ICONS = { desktop_1440: Monitor, tablet_768: Tablet, mobile_390: Smartphone };

export function RerunPanel({ sim, onClose }) {
  const navigate = useNavigate();
  const [env, setEnv] = useState(sim.environment_mode || "b2b_saas");
  const [viewport, setViewport] = useState(sim.viewport || "desktop_1440");
  const [authOpen, setAuthOpen] = useState(false);
  const [auth, setAuth] = useState({ type: "form", username: "", password: "", token: "", login_url: "" });
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const hasAuth = auth.username || auth.token || (auth.type === "form" && auth.login_url);
      const body = {
        target_url: sim.target_url,
        environment_mode: env,
        viewport_override: viewport,
        rerun_of: sim.sim_id,
        ...(hasAuth ? { auth_credentials: auth } : {}),
      };
      const res = await createSim(body);
      navigate(`/sim/s/${res.simulation_id}`);
    } catch (e) {
      if (e.code === "card_required") {
        toast.error("Please add a card to run simulations");
        navigate("/sim");
      } else if (e.code === "payment_failed") {
        toast.error("We couldn't charge your card — update your payment method.");
        navigate("/sim");
      } else {
        toast.error(e.message || "Could not start the re-run");
      }
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
      className="overflow-hidden mb-5"
      data-testid="sim-rerun-panel"
    >
      <div className="sim-card-elevated rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="sim-mono text-[11px] text-[#00F0FF] uppercase tracking-wider">Re-run &amp; compare</span>
          <span className="text-[#9AA0AC] text-xs truncate max-w-[50vw]">{sim.target_url}</span>
          <button data-testid="sim-rerun-close" onClick={onClose} className="ml-auto text-[#9AA0AC] hover:text-[#F5F5F5] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[#C2C5CE] text-[13px] mb-4">
          Apply your fixes to the page, then re-run the same URL. We'll score it again and show the before/after delta.
        </p>

        {/* Environment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(ENV_MODES).map(([k, v]) => {
            const Icon = k === "b2b_saas" ? Building2 : ShoppingBag;
            const active = env === k;
            return (
              <button
                key={k}
                data-testid={`sim-rerun-env-${k}`}
                onClick={() => setEnv(k)}
                className={cn(
                  "text-left rounded-lg border p-3 flex items-center gap-3 transition-colors",
                  active ? "border-[#00F0FF]/60 bg-[#00F0FF]/5" : "border-[#27272A] bg-[#0A0A0A] hover:border-[#3f3f46]"
                )}
              >
                <div className={cn("w-8 h-8 rounded-md flex items-center justify-center shrink-0", active ? "bg-[#00F0FF]/15" : "bg-white/5")}>
                  <Icon className={cn("w-4 h-4", active ? "text-[#00F0FF]" : "text-[#D4D4D8]")} />
                </div>
                <div>
                  <p className="text-[#F5F5F5] text-sm font-semibold">{v.label}</p>
                  <p className="text-[#C2C5CE] text-xs">{v.sub}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Viewport + auth */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="flex items-center gap-1.5 bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1">
            {Object.entries(VIEWPORTS).map(([k, v]) => {
              const Icon = VP_ICONS[k];
              const active = viewport === k;
              return (
                <button
                  key={k}
                  data-testid={`sim-rerun-viewport-${k}`}
                  onClick={() => setViewport(k)}
                  title={v.dims}
                  className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs transition-colors",
                    active ? "bg-[#1A1A1A] text-[#F5F5F5]" : "text-[#C2C5CE] hover:text-[#D4D4D8]")}
                >
                  <Icon className="w-3.5 h-3.5" /> {v.label}
                </button>
              );
            })}
          </div>
          <button
            data-testid="sim-rerun-auth-toggle"
            onClick={() => setAuthOpen((o) => !o)}
            className="flex items-center gap-1.5 sim-mono text-[11px] text-[#C2C5CE] hover:text-[#F5F5F5] transition-colors"
          >
            <Lock className="w-3 h-3" /> Sign-in / private access
            <ChevronDown className={cn("w-3 h-3 transition-transform", authOpen && "rotate-180")} />
          </button>
        </div>

        {authOpen && (
          <div className="sim-card rounded-lg p-3.5 space-y-3 mt-3">
            <p className="text-[#C2C5CE] text-[12px]">
              Credentials aren't stored between runs — re-enter them to score the signed-in page again.
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { t: "form", label: "Form login", icon: KeyRound },
                { t: "cookie", label: "Session cookies", icon: Cookie },
                { t: "basic", label: "HTTP Basic", icon: Lock },
              ].map(({ t, label, icon: Icon }) => (
                <button key={t} data-testid={`sim-rerun-auth-type-${t}`} onClick={() => setAuth({ ...auth, type: t })}
                  className={cn("sim-mono text-[11px] px-3 py-1.5 rounded border transition-colors flex items-center gap-1.5",
                    auth.type === t ? "border-[#00F0FF]/50 text-[#00F0FF] bg-[#00F0FF]/5" : "border-[#27272A] text-[#C2C5CE] hover:border-[#3f3f46]")}>
                  <Icon className="w-3 h-3" /> {label}
                </button>
              ))}
            </div>
            {auth.type === "form" && (
              <div className="space-y-2">
                <input data-testid="sim-rerun-auth-login-url" value={auth.login_url} onChange={(e) => setAuth({ ...auth, login_url: e.target.value })}
                  placeholder="Login page URL (optional — defaults to the target URL)"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="sim-rerun-auth-user" value={auth.username} onChange={(e) => setAuth({ ...auth, username: e.target.value })} placeholder="Email / username" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                  <input data-testid="sim-rerun-auth-pass" type="password" value={auth.password} onChange={(e) => setAuth({ ...auth, password: e.target.value })} placeholder="Password" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                </div>
              </div>
            )}
            {auth.type === "cookie" && (
              <textarea data-testid="sim-rerun-auth-cookie" value={auth.token} onChange={(e) => setAuth({ ...auth, token: e.target.value })} rows={2}
                placeholder="session=abc123; token=xyz789"
                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded px-3 py-2 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none resize-none" />
            )}
            {auth.type === "basic" && (
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="sim-rerun-auth-user" value={auth.username} onChange={(e) => setAuth({ ...auth, username: e.target.value })} placeholder="Username" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
                <input data-testid="sim-rerun-auth-pass" type="password" value={auth.password} onChange={(e) => setAuth({ ...auth, password: e.target.value })} placeholder="Password" className="bg-[#0A0A0A] border border-[#27272A] rounded px-3 h-9 text-sm text-[#F5F5F5] sim-mono placeholder:text-[#8E929C] focus:border-[#00F0FF]/40 focus:outline-none" />
              </div>
            )}
          </div>
        )}

        <button
          data-testid="sim-rerun-run-btn"
          onClick={run}
          disabled={loading}
          className="sim-tracing-btn mt-4 h-11 w-full sm:w-auto px-6 rounded-lg bg-[#00F0FF] text-[#0A0A0A] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#33f3ff] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
          {loading ? "Launching…" : "Run & compare"}
        </button>
      </div>
    </motion.div>
  );
}
